/**
 * Three-way merge of two edits to the same record.
 *
 * `base` is the version the client edited FROM; `server` is what the server holds now; `client` is
 * the client's edited version. Compared field by field (top level, excluding id/resourceType/meta):
 *  - changed on one side only            -> take that side
 *  - changed on both sides to the same   -> take it
 *  - changed on both sides differently   -> CONFLICT: keep the server value, record the client's,
 *                                           so a person reviews it. Nothing is silently lost.
 * A field absent on one side is treated as the value `undefined`, so deleting a field is an edit.
 */
const SKIP = new Set(['id', 'resourceType', 'meta']);
/** Deterministic JSON for equality: object keys sorted, undefined dropped. */
export function canonicalJson(v) {
    return JSON.stringify(v, (_k, val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const o = val;
            return Object.fromEntries(Object.keys(o)
                .sort()
                .map((k) => [k, o[k]]));
        }
        return val;
    });
}
export const deepEqual = (a, b) => canonicalJson(a) === canonicalJson(b);
export function threeWayMerge(base, server, client) {
    const merged = { ...server };
    const conflicts = [];
    const fields = new Set([...Object.keys(base), ...Object.keys(server), ...Object.keys(client)]);
    for (const f of [...fields].sort()) {
        if (SKIP.has(f))
            continue;
        const b = base[f];
        const s = server[f];
        const c = client[f];
        const clientChanged = !deepEqual(b, c);
        const serverChanged = !deepEqual(b, s);
        if (!clientChanged)
            continue; // keep server's value (changed or not)
        if (!serverChanged || deepEqual(s, c)) {
            if (c === undefined)
                delete merged[f];
            else
                merged[f] = c;
            continue;
        }
        conflicts.push({ field: f, base: b, server: s, client: c });
    }
    return { merged: merged, conflicts };
}
//# sourceMappingURL=merge.js.map